import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/axios';

export interface AuctionState {
  id: number;
  status: 'active' | 'concluded' | 'upcoming';
  item_name: string;
  item_image: string;
  auction_image: string;
  current_bid: number;
  current_user: string;
  min_increment: number;
  time_left: string;
  user_can_bid: boolean;
  winner: string;
  your_bid: number | null;
  auction_name: string;
  no_bids: boolean;
  auction_end: string | null;
}

export interface AuctionRewardItem {
  sn_name: string;
  url_image: string;
  codename128: string;
  quanty: number;
  gender: string;
}

export interface AuctionBidRow {
  bid_amount: number;
  CharName16: string;
  RefObjID: number;
}

export interface AuctionPayload {
  auction: AuctionState;
  auction_items: AuctionRewardItem[];
  auction_bids: AuctionBidRow[];
}

export const AUCTION_QUERY_KEY = ['auctionsystem'] as const;

export async function fetchAuction(): Promise<AuctionPayload> {
  const { data } = await api.get<AuctionPayload & { status: string }>('/api/v1/auctionsystem');
  return {
    auction: data.auction,
    auction_items: data.auction_items ?? [],
    auction_bids: data.auction_bids ?? [],
  };
}

export function useAuction() {
  return useQuery({
    queryKey: AUCTION_QUERY_KEY,
    queryFn: fetchAuction,
    staleTime: 15_000,
    refetchInterval: 30_000,
    placeholderData: (prev) => prev,
  });
}

export function useAuctionMutations() {
  const queryClient = useQueryClient();

  const placeBid = useMutation({
    mutationFn: async (payload: { auction_id: number; bid_amount: number }) => {
      const { data } = await api.post<{ success: boolean; message: string }>(
        '/api/v1/auctionsystem/bid',
        payload,
      );
      return data;
    },
    onSuccess: (data) => {
      if (data.success) {
        void queryClient.invalidateQueries({ queryKey: AUCTION_QUERY_KEY });
        void queryClient.invalidateQueries({ queryKey: ['silk-balance'] });
      }
    },
  });

  return { placeBid };
}

export function prefetchAuction(queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.prefetchQuery({
    queryKey: AUCTION_QUERY_KEY,
    queryFn: fetchAuction,
    staleTime: 15_000,
  });
}
